import { Injectable } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { AuthGuardService } from '../guard/auth-guard/auth-guard.service';
import * as roleClaims from './role-claims.util';
import { RoleTier } from './role-claims.util';

export type ApproverTier = 'Approver' | 'User' | 'Unauthorized';
export type { RoleTier };

/**
 * Single source of truth for deriving a user's affiliate code and app-level role from
 * their Azure AD role claims / Company Name claim.
 *
 * Before this service existed, this logic was duplicated (and had DRIFTED) across
 * app.component.ts (`resolveCompanyName` / `mapAffiliate`) and header.component.ts
 * (`getaffilate` + `userRoleCheck`, which itself had 3 near-identical role-classification
 * blocks). The two paths could resolve a DIFFERENT affiliate/role for the exact same user
 * depending on which code path ran first - e.g. header.component.ts's `getaffilate()` used
 * to fall back to the raw `roles[0]` (Azure AD does not guarantee role-claim ordering)
 * instead of preferring a "-User" role like app.component.ts did, and that same fallback
 * skipped the TMNA->TMN mapping entirely. All components MUST derive affiliate/role
 * through this service instead of re-implementing any part of this logic locally.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  constructor(private readonly authSrv: AuthGuardService) { }

  // SECURITY (AppSec finding: "Authorization role derived from client-modifiable storage"):
  // Returns the `roles` claim decoded FRESH from the in-memory bearer id token
  // (AuthGuardService.getIdToken(), never persisted to localStorage/sessionStorage) instead
  // of the separately-writable `localStorage['roles']` array used by
  // CommonService.getStoredRoles(). A DevTools call like
  // `localStorage.setItem('roles', JSON.stringify(['TMY-ADMIN']))` has no effect on this -
  // an attacker would need to actually tamper with the in-memory token object itself, which
  // isn't reachable via storage APIs. Does not re-verify the token's cryptographic signature
  // (that already happens at login/bootstrap/in-app-navigation checkpoints - see
  // JwtVerificationService) - this purely hardens WHERE authorization-driving role claims are
  // read FROM, not a replacement for server-side enforcement of every privileged operation.
  //
  // Renamed from `getVerifiedRoles()` (AppSec High Finding #8 assurance work, 2026-09-29):
  // the old name implied this method itself cryptographically verifies the token, which it
  // does not (it's a plain jwt-decode) - callers must not treat a claim read via this method
  // as proof of a valid signature on its own.
  getRolesFromIdToken(): string[] {
    const idToken = this.authSrv.getIdToken();
    if (!idToken) {
      return [];
    }
    try {
      const decoded: any = jwtDecode(idToken);
      return Array.isArray(decoded?.roles) ? decoded.roles : [];
    } catch {
      return [];
    }
  }

  getEmployeeIdFromIdToken(): string | null {
    const idToken = this.authSrv.getIdToken();
    if (!idToken) {
      return null;
    }
    try {
      const employeeId = jwtDecode<any>(idToken)?.['Employee ID'];
      return typeof employeeId === 'string' || typeof employeeId === 'number'
        ? String(employeeId).trim() || null
        : null;
    } catch {
      return null;
    }
  }

  getWorkEmailFromIdToken(): string | null {
    const idToken = this.authSrv.getIdToken();
    if (!idToken) {
      return null;
    }
    try {
      const email = jwtDecode<any>(idToken)?.['Work Email'];
      return typeof email === 'string' ? email.trim().toLowerCase() || null : null;
    } catch {
      return null;
    }
  }

  // SECURITY (AppSec Critical Finding #1 closure, round 2 - "Tenant identifier from URL
  // overrides claim-derived affiliate"): same rationale as getRolesFromIdToken() above, but for
  // the `email` claim. Decodes it FRESH from the in-memory bearer access token
  // (AuthGuardService.getAccessToken(), never persisted to localStorage) instead of the
  // separately-writable `localStorage['emailId']` string that ViewQuestionsComponent's
  // affiliate-authorization gate previously sent to checkApproverId/checkUserInHR - a
  // DevTools call like `localStorage.setItem('emailId', 'victim@example.com')` has no effect
  // on this. Use this (not `localStorage.getItem('emailId')`) for any lookup whose RESULT
  // drives an authorization decision; display-only uses of the email elsewhere are out of
  // scope for this method.
  getEmailFromAccessToken(): string | null {
    const accessToken = this.authSrv.getAccessToken();
    if (!accessToken) {
      return null;
    }
    try {
      const decoded: any = jwtDecode(accessToken);
      return decoded?.email ?? null;
    } catch {
      return null;
    }
  }

  /**
   * Normalizes a raw affiliate code (e.g. "TMNA") OR a full Company Name claim (e.g.
   * "Toyota Motor Europe NV/SA") into the canonical short affiliate code. Values with no
   * known synonym pass through unchanged. Delegates to role-claims.util (AppSec High
   * Finding #9 assurance work) so AuthGuardService can apply the IDENTICAL rule without a
   * circular DI dependency on this service.
   */
  mapAffiliate(value: string): string {
    return roleClaims.mapAffiliate(value);
  }

  /**
   * Extracts the affiliate-code portion of a single "AFFILIATE-Role" claim string, e.g.
   * "TMN-Admin" -> "TMN". Centralizes the `element.replace(/-\S+/g, '')` parsing that was
   * previously duplicated at 5+ call sites across app.component.ts and header.component.ts.
   */
  extractAffiliateCode(roleClaim: string): string {
    return roleClaims.extractAffiliateCode(roleClaim);
  }

  /**
   * Resolves the single canonical affiliate code for a user from their raw role claims.
   * Prefers a role claim containing "User" (the account's base/home affiliate membership)
   * over roles[0], since Azure AD does not guarantee role-claim ordering.
   */
  resolveAffiliateCode(roles: string[] | null | undefined): string {
    return roleClaims.resolveAffiliateCode(roles);
  }

  /**
   * Computes the normalized, UPPERCASED set of affiliate-scoped roles used for
   * admin/oversight badge and permission display. A role claim is included when it's
   * Admin/Oversight (always shown), or when it's a User claim on an account that also has
   * an Admin/Oversight role (mixed-role account), or when it's a User claim on an account
   * with no Admin/Oversight role at all.
   */
  computeAffiliateRoles(roles: string[] | null | undefined): string[] {
    if (!roles || roles.length === 0) {
      return [];
    }
    const rolesUpper = roles.map((r) => r.toUpperCase());
    const hasAdmin = rolesUpper.some((r) => r.includes('ADMIN'));
    const hasOversight = rolesUpper.some((r) => r.includes('OVERSIGHT'));
    const result: string[] = [];
    roles.forEach((role) => {
      const isAdminOrOversight = role.includes('Admin') || role.includes('Oversight');
      const isUser = role.includes('User');
      if (isAdminOrOversight || (isUser && (hasAdmin || hasOversight)) || (isUser && !hasAdmin && !hasOversight)) {
        result.push(this.mapAffiliate(this.extractAffiliateCode(role)).toUpperCase());
      }
    });
    return result;
  }

  /**
   * Classifies the app-level role tier purely from the role claims + the affiliate the
   * user is currently scoped to. Admin/Oversight are fully determined by claims alone.
   * A return of 'User' means the claims show ordinary membership only - the final
   * Approver/User/Unauthorized split still requires the caller's own is_approver lookup
   * (via `resolveApproverTier`). Returns null when no claim matches this affiliate at all.
   */
  classifyRoleTier(roles: string[] | null | undefined, affiliate: string): RoleTier | null {
    return roleClaims.classifyRoleTier(roles, affiliate);
  }

  /**
   * Classifies an `is_approver` lookup result (from checkApproverId/checkUserInHR) into a
   * final role tier. Centralizes what was previously duplicated - and INCONSISTENT - branch
   * handling: one call site only distinguished Approver vs User (silently treating an
   * inconclusive/missing lookup result AS "User"), while another also distinguished a
   * genuinely inconclusive lookup as 'Unauthorized'. Same input shape, different verdicts
   * depending only on which code path ran.
   */
  resolveApproverTier(payload: any[] | null | undefined): ApproverTier {
    const entry = payload?.[0];
    if (!entry) {
      return 'Unauthorized';
    }
    return entry.is_approver === true ? 'Approver' : 'User';
  }
}
